import { z } from 'zod';
import { Currency, IsoDatetime } from './primitives.js';
import { TrustLevel } from './trust.js';

// ULID varchar(40) per the data-model conventions (01-data-model §0).
const Id = z.string().max(40);

// State machine (01 §1, 02 §2.1): new → triaged → planned → executing ⇄
// awaiting_owner → verifying → closed; executing → blocked → escalated;
// cancelled is reachable from any state, owner-initiated only.
export const SituationState = z.enum([
  'new',
  'triaged',
  'planned',
  'executing',
  'awaiting_owner',
  'verifying',
  'closed',
  'blocked',
  'escalated',
  'cancelled',
]);
export type SituationState = z.infer<typeof SituationState>;

export const SituationKind = z.enum(['one_off', 'recurring', 'monitor']);
export type SituationKind = z.infer<typeof SituationKind>;

export const SituationSource = z.enum(['owner_message', 'protocol', 'engine', 'staff_message']);
export type SituationSource = z.infer<typeof SituationSource>;

// Closure = proof (constitution §4): the kind of evidence that was verified.
export const SituationProofKind = z.enum([
  'photo',
  'document',
  'tracking_number',
  'confirmation_message',
  'none',
]);
export type SituationProofKind = z.infer<typeof SituationProofKind>;

// Human takeover (05 F-20): while set, the engine stops autonomous actions
// on this situation — "no two pilots". NULL = the engine holds command.
export const OwnerOverride = z.enum(['catherine', 'owner']);
export type OwnerOverride = z.infer<typeof OwnerOverride>;

export const Situation = z.object({
  id: Id,
  household_id: Id,
  // S66: a household can own two homes — each situation knows which one.
  home_entity_id: Id.nullable(),
  state: SituationState.default('new'),
  kind: SituationKind.default('one_off'),
  source: SituationSource,
  // The original opening message, so the Catherine-test can replay it.
  opening_message: z.string().nullable(),
  opened_by_user_id: Id.nullable(),
  // The entity the situation is about; NULL = household-wide.
  subject_entity_id: Id.nullable(),
  title: z.string().min(1),
  // The typed understanding object (UnderstandResult) as stored.
  intent: z.record(z.any()).default({}),
  // LangGraph Postgres checkpoint — awaiting_owner resumes from here.
  graph_checkpoint_id: z.string().nullable(),
  // Trust level required for autonomous closure (0-4); child emergencies
  // are always 3-4 (D-S1).
  required_trust_level: TrustLevel.default(2),
  // Per-situation autonomous spend cap in minor units (S16); NULL = ladder.
  autonomous_budget_minor: z.number().int().nullable(),
  currency: Currency.nullable(),
  verify_by: IsoDatetime.nullable(),
  proof_kind: SituationProofKind.nullable(),
  proof_r2_keys: z.array(z.string()).default([]),
  closed_at: IsoDatetime.nullable(),
  // One short closing line — what the owner hears ("Closed — Y, Fri 20:00").
  closing_summary: z.string().nullable(),
  owner_override: OwnerOverride.nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
  deleted_at: IsoDatetime.nullable(),
});
export type Situation = z.infer<typeof Situation>;

// Append-only journal of every transition/action (01 §1.2, 02 §2.3) — the
// standing answer to "what happened with this in the end?".
export const SituationEventKind = z.enum([
  'transition',
  'note',
  'option_presented',
  'option_chosen',
  'verification',
  'escalation',
]);
export type SituationEventKind = z.infer<typeof SituationEventKind>;

export const SituationEvent = z.object({
  id: Id,
  situation_id: Id,
  household_id: Id,
  kind: SituationEventKind,
  from_state: SituationState.nullable(),
  to_state: SituationState.nullable(),
  actor_user_id: Id.nullable(),
  actor_entity_id: Id.nullable(),
  payload: z.record(z.any()).default({}),
  // No updated_at/deleted_at — append-only.
  created_at: IsoDatetime,
});
export type SituationEvent = z.infer<typeof SituationEvent>;

export const SituationOptionOutcome = z.enum(['chosen', 'rejected', 'expired']);
export type SituationOptionOutcome = z.infer<typeof SituationOptionOutcome>;

// One option row of a decision card (01 §1.3). The constitution-level
// invariant (2-3 options, exactly one recommendation) is enforced across a
// round by SituationOptionRound below.
export const SituationOption = z.object({
  id: Id,
  situation_id: Id,
  household_id: Id,
  // Discussion round: an owner rejection (S2) opens a new round.
  round: z.number().int().min(1).default(1),
  label: z.string().min(1),
  details: z.record(z.any()).default({}),
  price_minor: z.number().int().nullable(),
  currency: Currency.nullable(),
  recommended: z.boolean().default(false),
  recommendation_reason: z.string().nullable(),
  outcome: SituationOptionOutcome.nullable(),
  chosen_by_user_id: Id.nullable(),
  chosen_at: IsoDatetime.nullable(),
  created_at: IsoDatetime,
});
export type SituationOption = z.infer<typeof SituationOption>;

// Constitution §2 in code: every stop at awaiting_owner is a card of 2-3
// options in ONE round with exactly one recommendation — never an open
// question. Enforced here so an invalid card cannot cross the boundary.
export const SituationOptionRound = z
  .array(SituationOption)
  .min(2, 'a decision card needs at least 2 options')
  .max(3, 'a decision card has at most 3 options')
  .refine(
    (options) => options.every((option) => option.round === options[0]!.round),
    'all options of a card belong to one round',
  )
  .refine(
    (options) => options.filter((option) => option.recommended).length === 1,
    'exactly one option must be recommended',
  );
export type SituationOptionRound = z.infer<typeof SituationOptionRound>;

// --- Typed LLM outputs (02 §3.3) — the contract between free thinking and
// the rigid skeleton. A schema failure = node failure → retry, never "let it
// slide".

export const UnderstandResult = z.object({
  intent: z.string().min(1),
  // Anchoring in the house graph — "Rada" is an entity, not text.
  subject_entity_ids: z.array(Id),
  slots: z.record(z.any()),
  missing: z.array(z.string()),
  // Minimal gap-filling — not an interrogation.
  clarifying_questions: z.array(z.string()).max(2),
  confidence: z.number().min(0).max(1),
});
export type UnderstandResult = z.infer<typeof UnderstandResult>;

export const PlanStep = z.object({
  id: z.string().min(1),
  // An 03-actuation tool only — the allowlist is enforced by the gate.
  tool: z.string().min(1),
  args: z.record(z.any()),
  depends_on: z.array(z.string()),
  // The LLM's estimate; the gate verifies against the trust profile.
  trust_required: TrustLevel,
  estimated_cost_minor: z.number().int().optional(),
});
export type PlanStep = z.infer<typeof PlanStep>;

export const SituationPlan = z.object({
  steps: z.array(PlanStep).min(1),
  // Step ids that must pass owner_gate.
  approval_points: z.array(z.string()),
  // Without a verification spec the plan is rejected (decision 2).
  verification_spec: z.object({
    evidence_kind: z.enum([
      'confirmation_message',
      'photo',
      'document',
      'tracking_number',
      'ledger_entry',
    ]),
    timeout_minutes: z.number().int().positive(),
    success_signal: z.string().min(1),
  }),
  blueprint_ref: z.string().optional(),
});
export type SituationPlan = z.infer<typeof SituationPlan>;

// The owner_gate card (02 §3.3) — the constitution enforced in the schema
// itself: 2-3 options, exactly one recommended.
export const OwnerDecisionCard = z.object({
  question: z.string().min(1),
  options: z
    .array(
      z.object({
        label: z.string().min(1),
        details: z.string(),
        price_minor: z.number().int().nullable(),
        recommended: z.boolean(),
        reason: z.string().optional(),
      }),
    )
    .min(2)
    .max(3)
    .refine(
      (options) => options.filter((option) => option.recommended).length === 1,
      'exactly one recommendation',
    ),
});
export type OwnerDecisionCard = z.infer<typeof OwnerDecisionCard>;

// A commitment creates a lock (decision 10): a resource held in a time
// window by one owner's instruction (01 §1.4, 02 §5).
export const CommitmentState = z.enum(['active', 'released', 'fulfilled']);
export type CommitmentState = z.infer<typeof CommitmentState>;

export const Commitment = z.object({
  id: Id,
  household_id: Id,
  // The held resource: driver, vehicle, petty cash, an owner time window.
  resource_entity_id: Id,
  window: z
    .object({ start: IsoDatetime, end: IsoDatetime })
    .refine((window) => window.end > window.start, 'window end must be after start'),
  // Which owner committed (decision 1: both equal). Only they can release.
  committed_by_user_id: Id,
  situation_id: Id,
  state: CommitmentState.default('active'),
  released_at: IsoDatetime.nullable(),
  // Must equal committed_by_user_id (02 §5.3) — enforced by the service.
  release_approved_by_user_id: Id.nullable(),
  summary: z.string().nullable(),
  created_at: IsoDatetime,
});
export type Commitment = z.infer<typeof Commitment>;
